<div data-spartan-page class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <app-page-header
    eyebrow="Admin"
    title="Security Dashboard"
    subtitle="Monitor sessions, security events, and user activity."
  >
    <div actions class="flex items-center gap-2">
      <label class="flex items-center gap-2 cursor-pointer select-none text-xs text-muted-foreground">
        <input type="checkbox" [(ngModel)]="autoRefresh" (change)="toggleAutoRefresh()" class="w-4 h-4 rounded accent-primary" aria-label="Auto-refresh">
        <span>Auto ({{ refreshInterval }}s)</span>
      </label>
      <button hlmBtn variant="outline" size="sm" class="gap-2" (click)="exportAuditReport()">Export Report</button>
      <button hlmBtn size="sm" class="gap-2" (click)="refreshAll()" [disabled]="loading">
        @if (loading) {
          <span class="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        } @else {
          <ng-icon hlm name="lucideRefreshCw" class="size-4" />
        }
        Refresh
      </button>
    </div>
  </app-page-header>

  <!-- Loading overlay -->
  @if (loading) {
    <div class="flex items-center justify-center py-10 gap-3 text-muted-foreground">
      <span class="inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
      <span class="text-sm">Loading dashboard data…</span>
    </div>
  }

  <!-- Overview KPI row -->
  @if (dashboardData) {
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
          <ng-icon hlm name="lucideUsers" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight">{{ dashboardData.totalUsers }}</div>
          <div class="text-[11px] text-muted-foreground">Total Users</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400 shrink-0">
          <ng-icon hlm name="lucideCheck" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight text-green-700 dark:text-green-400">{{ dashboardData.activeUsers }}</div>
          <div class="text-[11px] text-muted-foreground">Active Users</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
          <ng-icon hlm name="lucideLock" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight">{{ dashboardData.activeSessions }}</div>
          <div class="text-[11px] text-muted-foreground">Active Sessions</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
          [class]="getFailedLoginIconClass(dashboardData.failedLogins)">
          <ng-icon hlm name="lucideAlertTriangle" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight"
             [class]="getFailedLoginValueClass(dashboardData.failedLogins)">{{ dashboardData.failedLogins }}</div>
          <div class="text-[11px] text-muted-foreground">Failed Logins</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
          [class]="getCriticalEventIconClass(dashboardData.criticalEvents)">
          <ng-icon hlm name="lucideShieldAlert" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight"
             [class]="getCriticalEventValueClass(dashboardData.criticalEvents)">{{ dashboardData.criticalEvents }}</div>
          <div class="text-[11px] text-muted-foreground">Critical Events</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
          [class]="getSystemHealthIconClass(dashboardData.systemHealth)">
          <ng-icon hlm name="lucideActivity" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight"
             [class]="getSystemHealthValueClass(dashboardData.systemHealth)">
            {{ dashboardData.systemHealth | titlecase }}
          </div>
          <div class="text-[11px] text-muted-foreground">System Health</div>
        </div>
      </div>
    </div>
  }

  <!-- Session Metrics row -->
  @if (sessionMetrics) {
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
          <ng-icon hlm name="lucideLock" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight text-accent">{{ sessionMetrics.totalActiveSessions }}</div>
          <div class="text-[11px] text-muted-foreground">Active Sessions</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400 shrink-0">
          <ng-icon hlm name="lucideUsers" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight text-green-700 dark:text-green-400">{{ sessionMetrics.uniqueActiveUsers }}</div>
          <div class="text-[11px] text-muted-foreground">Active Users</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 shrink-0">
          <ng-icon hlm name="lucideAlertTriangle" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight text-amber-700 dark:text-amber-400">{{ sessionMetrics.suspiciousSessions }}</div>
          <div class="text-[11px] text-muted-foreground">Suspicious</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300 shrink-0">
          <ng-icon hlm name="lucideBarChart2" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight">{{ sessionMetrics.peakSessionsToday }}</div>
          <div class="text-[11px] text-muted-foreground">Peak Today</div>
        </div>
      </div>
      <div hlmCard class="p-4 flex items-center gap-3">
        <span class="grid place-items-center w-9 h-9 rounded-md bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300 shrink-0">
          <ng-icon hlm name="lucideClock" class="size-4" />
        </span>
        <div>
          <div class="text-xl font-semibold tabular-nums tracking-tight">{{ sessionMetrics.averageSessionDuration }}h</div>
          <div class="text-[11px] text-muted-foreground">Avg Duration</div>
        </div>
      </div>
    </div>
  }

  <!-- Main 3-column grid -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">

    <!-- Active Sessions Panel -->
    <div hlmCard class="flex flex-col">
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <p class="text-base font-semibold leading-tight">Active Sessions</p>
        <div class="flex items-center gap-1.5 flex-wrap">
          <button hlmBtn variant="outline" size="sm" class="h-7 text-xs" (click)="selectAllSessions()" [disabled]="activeSessions.length === 0">All</button>
          <button hlmBtn variant="outline" size="sm" class="h-7 text-xs" (click)="clearSessionSelection()" [disabled]="selectedSessions.size === 0">Clear</button>
          <button hlmBtn size="sm" class="h-7 text-xs bg-red-600 text-white hover:bg-red-500" (click)="bulkInvalidateSessions()" [disabled]="selectedSessions.size === 0">
            Terminate ({{ selectedSessions.size }})
          </button>
          <button hlmBtn variant="outline" size="sm" class="h-7 w-7 p-0 appearance-none focus-visible:ring-2 focus-visible:ring-accent rounded-md" (click)="loadActiveSessions()" [disabled]="sessionsLoading" aria-label="Refresh sessions">
            <ng-icon hlm name="lucideRefreshCw" class="size-3.5" />
          </button>
        </div>
      </div>

      <!-- Session Filters -->
      <div class="px-4 py-2.5 border-b border-border/60 flex flex-wrap gap-2 bg-surface-raised/60">
        <input type="text"
          class="flex-1 min-w-[120px] h-8 rounded-md border border-border-strong bg-surface text-xs px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
          placeholder="Filter IP…"
          [(ngModel)]="sessionFilters.ipAddress">
        <input type="text"
          class="flex-1 min-w-[120px] h-8 rounded-md border border-border-strong bg-surface text-xs px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
          placeholder="Filter User Agent…"
          [(ngModel)]="sessionFilters.userAgent">
        <div class="relative">
          <select
            class="appearance-none h-8 rounded-md border border-border-strong bg-surface text-xs pl-2 pr-7 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition w-28"
            [(ngModel)]="sessionFilters.timeRange">
            <option value="all">All Time</option>
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <ng-icon hlm name="lucideChevronDown" class="absolute right-1.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground pointer-events-none" />
        </div>
        <label class="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" class="w-3.5 h-3.5 rounded accent-primary" [(ngModel)]="sessionFilters.showSuspiciousOnly">
          Suspicious
        </label>
        <button hlmBtn size="sm" class="h-8 text-xs" (click)="loadActiveSessions()">Apply</button>
      </div>

      <div class="flex-1 p-4 space-y-2 overflow-y-auto max-h-[480px]">
        @if (sessionsLoading) {
          <div class="flex flex-col items-center justify-center py-8 gap-3 text-muted-foreground">
            <span class="inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            <span class="text-xs">Loading sessions…</span>
          </div>
        }
        @if (!sessionsLoading && getFilteredSessions().length === 0) {
          <div class="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
            <p class="text-sm">No active sessions found</p>
          </div>
        }
        @for (session of getFilteredSessions(); track session.id) {
          <div class="rounded-md border p-3 text-xs space-y-2 transition-colors"
               [class]="getSessionCardClass(session)"
               [class.selected]="selectedSessions.has(session.id)">
            <div class="flex items-start justify-between gap-2">
              <div class="flex items-start gap-2">
                <input type="checkbox" class="w-3.5 h-3.5 rounded accent-primary mt-0.5"
                  [checked]="selectedSessions.has(session.id)"
                  (change)="toggleSessionSelection(session.id)"
                  aria-label="Select session">
                <div>
                  <p class="font-mono text-[10.5px] text-muted-foreground">{{ session.id.substring(0, 8) }}…</p>
                  <span class="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium mt-0.5"
                    [class]="getSessionRiskBadgeClass(session)">
                    {{ getSessionRiskLevel(session).toUpperCase() }} RISK
                  </span>
                </div>
              </div>
              <div class="flex gap-1 shrink-0">
                <button hlmBtn variant="outline" size="sm" class="h-6 text-[10px] px-2 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/30 hover:bg-amber-50 dark:hover:bg-amber-500/10" (click)="flagSessionAsSuspicious(session.id)" title="Flag as Suspicious">Flag</button>
                <button hlmBtn size="sm" class="h-6 text-[10px] px-2 bg-red-600 text-white hover:bg-red-500" (click)="terminateSession(session.id)" title="Terminate">End</button>
              </div>
            </div>
            <div class="flex items-center gap-1.5 text-muted-foreground">
              <span>{{ getDeviceInfo(session.userAgent).device }}</span>
              <span>&bull;</span>
              <span>{{ getDeviceInfo(session.userAgent).browser }}</span>
              <span>&bull;</span>
              <span>{{ getDeviceInfo(session.userAgent).os }}</span>
            </div>
            <div class="grid grid-cols-2 gap-x-3 gap-y-1">
              <div><p class="text-[10px] text-muted-foreground">IP</p>
                <p class="font-mono text-[10.5px]">{{ session.ipAddress }}</p></div>
              <div><p class="text-[10px] text-muted-foreground">Location</p>
                <p>{{ getLocationFromIP(session.ipAddress) }}</p></div>
              <div><p class="text-[10px] text-muted-foreground">Created</p>
                <p>{{ session.createdAtUtc | date:'short' }}</p></div>
              <div><p class="text-[10px] text-muted-foreground">Last Active</p>
                <p>{{ session.lastActivityAtUtc ? getTimeAgo(session.lastActivityAtUtc) : '—' }}</p></div>
              <div class="col-span-2"><p class="text-[10px] text-muted-foreground">Expires</p>
                <p>{{ session.expiresAtUtc | date:'medium' }}</p></div>
            </div>
          </div>
        }
      </div>
      @if (getFilteredSessions().length > 0) {
        <div class="px-4 py-2.5 border-t border-border text-[11px] text-muted-foreground">
          Showing {{ getFilteredSessions().length }} of {{ activeSessions.length }} &bull;
          Selected: {{ selectedSessions.size }} &bull;
          High: {{ getHighRiskSessionsCount() }} &bull;
          Med: {{ getMediumRiskSessionsCount() }} &bull;
          Low: {{ getLowRiskSessionsCount() }}
        </div>
      }
    </div>

    <!-- Security Events Panel -->
    <div hlmCard class="flex flex-col">
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <p class="text-base font-semibold leading-tight">Security Events</p>
        <div class="flex items-center gap-1.5">
          <div class="relative">
            <select
              class="appearance-none h-8 rounded-md border border-border-strong bg-surface text-xs pl-2 pr-7 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition w-32"
              [(ngModel)]="selectedEventType" (change)="onEventTypeChange()">
              <option value="all">All Types</option>
              @for (type of eventTypes; track type) {
                <option [value]="type">{{ getEventTypeDisplay(type) }}</option>
              }
            </select>
            <ng-icon hlm name="lucideChevronDown" class="absolute right-1.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground pointer-events-none" />
          </div>
          <div class="relative">
            <select
              class="appearance-none h-8 rounded-md border border-border-strong bg-surface text-xs pl-2 pr-7 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition w-28"
              [(ngModel)]="selectedSeverity" (change)="onSeverityChange()">
              <option value="all">All Levels</option>
              @for (severity of severityLevels; track severity) {
                <option [value]="severity">{{ severity }}</option>
              }
            </select>
            <ng-icon hlm name="lucideChevronDown" class="absolute right-1.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground pointer-events-none" />
          </div>
          <button hlmBtn variant="outline" size="sm" class="h-8 w-8 p-0 appearance-none focus-visible:ring-2 focus-visible:ring-accent rounded-md" (click)="loadRecentEvents()" [disabled]="eventsLoading" aria-label="Refresh events">
            <ng-icon hlm name="lucideRefreshCw" class="size-3.5" />
          </button>
        </div>
      </div>
      <div class="flex-1 p-4 space-y-2 overflow-y-auto max-h-[520px]">
        @if (eventsLoading) {
          <div class="flex items-center justify-center py-8 text-muted-foreground text-xs">Loading events…</div>
        }
        @if (!eventsLoading && recentEvents.length === 0) {
          <div class="flex items-center justify-center py-8 text-muted-foreground text-xs">No security events found</div>
        }
        @for (event of recentEvents; track $index) {
          <div class="rounded-md border border-border p-3 text-xs space-y-1.5">
            <div class="flex items-start justify-between gap-2">
              <span class="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                [class]="getEventSeverityBadgeClass(event.severity)">
                {{ event.severity }}
              </span>
              <span class="text-[10px] text-muted-foreground">{{ getTimeAgo(event.createdAtUtc) }}</span>
            </div>
            <p class="font-medium">{{ getEventTypeDisplay(event.eventType) }}</p>
            <p class="text-muted-foreground">{{ event.description }}</p>
            <div class="flex items-center gap-3 text-[10px] text-muted-foreground">
              @if (event.ipAddress) { <span class="font-mono">{{ event.ipAddress }}</span> }
              <span class="inline-flex items-center rounded-md px-1.5 py-0.5"
                [class]="getEventResolvedBadgeClass(event.isResolved)">
                {{ event.isResolved ? 'Resolved' : 'Open' }}
              </span>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Recent Activity Panel -->
    <div hlmCard class="flex flex-col">
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
        <p class="text-base font-semibold leading-tight">Recent Activity</p>
        <button hlmBtn variant="outline" size="sm" class="h-8 w-8 p-0 appearance-none focus-visible:ring-2 focus-visible:ring-accent rounded-md" (click)="loadRecentActivity()" [disabled]="activityLoading" aria-label="Refresh activity">
          <ng-icon hlm name="lucideRefreshCw" class="size-3.5" />
        </button>
      </div>
      <div class="flex-1 p-4 space-y-2 overflow-y-auto max-h-[520px]">
        @if (activityLoading) {
          <div class="flex items-center justify-center py-8 text-muted-foreground text-xs">Loading activity…</div>
        }
        @if (!activityLoading && recentActivity.length === 0) {
          <div class="flex items-center justify-center py-8 text-muted-foreground text-xs">No recent activity found</div>
        }
        @for (activity of recentActivity; track $index) {
          <div class="rounded-md border border-border p-3 text-xs space-y-1">
            <div class="flex items-start justify-between gap-2">
              <p class="font-medium">
                {{ activity.action }}
                <span class="font-normal text-muted-foreground"> {{ activity.resource }}</span>
                @if (activity.resourceId) { <span class="font-mono text-[10.5px]"> #{{ activity.resourceId }}</span> }
              </p>
              <span class="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium shrink-0"
                [class]="getActivitySuccessBadgeClass(activity.success)">
                {{ activity.success ? 'Success' : 'Failed' }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span class="font-mono">{{ activity.ipAddress }}</span>
              <span>&bull;</span>
              <span>{{ getTimeAgo(activity.createdAtUtc) }}</span>
            </div>
            @if (activity.details) {
              <p class="text-[10.5px] text-muted-foreground">{{ activity.details }}</p>
            }
            @if (!activity.success && activity.errorMessage) {
              <p class="text-[10.5px] text-red-600 dark:text-red-400">{{ activity.errorMessage }}</p>
            }
          </div>
        }
      </div>
    </div>
  </div>

  @if (dashboardData) {
    <p class="text-[11px] text-muted-foreground text-right">Last updated: {{ getTimeAgo(dashboardData.lastUpdated) }}</p>
  }
</div>
